import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { costingsAPI } from '../../services/api';
import { printStyles, CompanyLetterhead } from './shared';

export function CostingPrintView() {
  const { id } = useParams();
  const [costing, setCosting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [printTriggered, setPrintTriggered] = useState(false);

  // Check if opened in preview mode (inside modal iframe)
  const isPreviewMode = new URLSearchParams(window.location.search).get('preview') === 'true';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await costingsAPI.get(id);
        // API returns {costing: {...}, breakdown: {...}}
        const costingData = res.data.costing || res.data;
        setCosting(costingData);
        setLoading(false);
      } catch (err) {
        setError("Failed to load costing");
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  // Separate useEffect for print - only triggers when data is ready and NOT in preview mode
  useEffect(() => {
    if (!loading && costing && !printTriggered && !isPreviewMode) {
      setPrintTriggered(true);
      // Wait 500ms for DOM to paint before triggering print
      const timer = setTimeout(() => {
        window.print();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [loading, costing, printTriggered, isPreviewMode]);

  const formatCurrency = (amount) => {
    // Always show exactly 4 decimal places for consistency
    return `₹${(amount || 0).toFixed(4)}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  // Calculate Cost/Kg from Cost/Piece - always 4 decimal places
  const getCostPerKg = (costPerPiece) => {
    const netWeightKg = (costing?.net_weight_grams || 0) / 1000;
    if (netWeightKg <= 0) return "-";
    return `₹${((costPerPiece || 0) / netWeightKg).toFixed(4)}`;
  };

  // Get setup/tooling costs for a specific process step by process_step_id
  const getSetupCostForStep = (processCost) => {
    const setupCosts = costing?.setup_costs || [];
    const stepId = processCost?.process_step_id;
    if (!stepId) return null;
    return setupCosts.find(sc => sc.process_step_id === stepId);
  };

  const getToolingCostForStep = (processCost) => {
    const toolingCosts = costing?.tooling_costs || [];
    const stepId = processCost?.process_step_id;
    if (!stepId) return null;
    return toolingCosts.find(tc => tc.process_step_id === stepId);
  };

  // Check if a setup/tooling cost has been linked to any process
  const isSetupCostLinked = (sc) => {
    const stepId = sc?.process_step_id;
    if (!stepId) return false;
    return (costing?.process_costs || []).some(pc => pc.process_step_id === stepId);
  };

  const isToolingCostLinked = (tc) => {
    const stepId = tc?.process_step_id;
    if (!stepId) return false;
    return (costing?.process_costs || []).some(pc => pc.process_step_id === stepId);
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'white',
        fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif'
      }}>
        <style>{printStyles}</style>
        <div className="spinner" style={{ marginBottom: '20px' }}></div>
        <p style={{ color: '#1e40af', fontSize: '18px', fontWeight: '600' }}>
          Generating Cost Sheet...
        </p>
        <p style={{ color: '#64748b', fontSize: '14px' }}>
          Please wait while we prepare your document
        </p>
      </div>
    );
  }

  if (error || !costing) {
    return (
      <div className="print-container">
        <style>{printStyles}</style>
        <p style={{ color: "red", textAlign: "center" }}>{error || "Costing not found"}</p>
      </div>
    );
  }

  // Extract nested data with proper fallbacks
  const rawMaterial = costing.raw_material || {};
  const packaging = costing.packaging_logistics || {};
  const quality = costing.quality || {};
  const rejection = costing.rejection || {};
  const overhead = costing.overhead || {};
  const financeRisk = costing.finance_risk || {};
  
  const netWeightGrams = costing.net_weight_grams || 0;
  const netWeightKg = netWeightGrams / 1000;
  
  // Calculate Gross Weight using formula: Gross Weight = Net Weight / (1 - Scrap%)
  const scrapPercentage = rawMaterial.scrap_percentage || 0;
  const scrapFactor = 1 - (scrapPercentage / 100);
  const grossWeightGrams = scrapFactor > 0 ? netWeightGrams / scrapFactor : netWeightGrams;
  
  // Calculate total process cost (process + setup + tooling)
  const processCostTotal = (costing.process_costs || []).reduce((sum, p) => sum + (p.cost_per_piece || 0), 0);
  const setupCostTotal = (costing.setup_costs || []).reduce((sum, s) => sum + (s.setup_cost_per_piece || s.cost_per_piece || 0), 0);
  const toolingCostTotal = (costing.tooling_costs || []).reduce((sum, t) => sum + (t.cost_per_piece || 0), 0);
  const totalProcessCost = processCostTotal + setupCostTotal + toolingCostTotal;
  
  // Get values from nested objects
  const rmCostPerPiece = rawMaterial.raw_material_cost_per_piece || 0;
  const packagingCostPerPiece = packaging.packaging_cost_per_piece || 0;
  const logisticsCostPerPiece = packaging.logistics_cost_per_piece || 0;
  const qualityCostPerPiece = quality.quality_cost_per_piece || 0;
  const rejectionCostPerPiece = rejection.rejection_cost_per_piece || 0;
  const overheadCostPerPiece = overhead.overhead_cost_per_piece || 0;
  const financeRiskCostPerPiece = financeRisk.finance_risk_cost_per_piece || 0;

  return (
    <div className="print-container">
      <style>{printStyles}</style>
      
      {/* Professional Letterhead */}
      <CompanyLetterhead 
        documentType="COSTING"
        documentNumber={costing.costing_number}
        revisionNumber={0}
      />

      {/* Part & Costing Info */}
      <div className="info-grid">
        <div className="info-box">
          <h4>Part Details</h4>
          <p><span className="value">{costing.part_internal_no}</span></p>
          <p>{costing.part_description || "No description"}</p>
          <p>Net Weight: <span className="value">{netWeightGrams || "-"} g ({netWeightKg.toFixed(4)} kg)</span></p>
          <p>Gross Weight: <span className="value">{grossWeightGrams.toFixed(2)} g</span> 
            <span style={{ fontSize: '10px', color: '#64748b' }}> (Net ÷ {scrapFactor.toFixed(3)} at {scrapPercentage}% scrap)</span>
          </p>
        </div>
        <div className="info-box">
          <h4>Costing Info</h4>
          <p>Version: <span className="value">{costing.version_number}</span></p>
          <p>Quantity: <span className="value">{(costing.quantity || 0).toLocaleString()} pcs</span></p>
          <p>Status: <span className="value">{costing.status}</span></p>
          <p>Date: <span className="value">{formatDate(costing.created_at)}</span></p>
        </div>
      </div>

      {/* Raw Material Section */}
      <h3 style={{ fontSize: "14px", fontWeight: "bold", marginBottom: "12px", marginTop: "20px", color: "#1e40af", borderBottom: "2px solid #1e40af", paddingBottom: "6px" }}>
        1. RAW MATERIAL COST
      </h3>
      <table className="items-table">
        <thead>
          <tr>
            <th>Parameter</th>
            <th className="number">Cost/Piece (₹)</th>
            <th className="number">Cost/Kg (₹)</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <div>RM Grade: <strong>{rawMaterial.rm_grade || "-"}</strong></div>
              <div style={{ fontSize: '10px', color: '#64748b' }}>
                Price: {formatCurrency(rawMaterial.rm_price_per_kg)}/kg | 
                Gross: {rawMaterial.gross_weight_grams?.toFixed(2) || 0}g | 
                Scrap: {rawMaterial.scrap_percentage || 0}% | 
                Recovery: {formatCurrency(rawMaterial.scrap_recovery_per_kg)}/kg
              </div>
            </td>
            <td className="number">{formatCurrency(rmCostPerPiece)}</td>
            <td className="number">{getCostPerKg(rmCostPerPiece)}</td>
          </tr>
        </tbody>
      </table>

      {/* Process Cost Section with Setup/Tooling as sub-rows */}
      <h3 style={{ fontSize: "14px", fontWeight: "bold", marginBottom: "12px", marginTop: "20px", color: "#1e40af", borderBottom: "2px solid #1e40af", paddingBottom: "6px" }}>
        2. PROCESS COSTS
      </h3>
      <table className="items-table">
        <thead>
          <tr>
            <th style={{ width: '40%' }}>Process</th>
            <th style={{ width: '15%' }}>Type</th>
            <th className="number" style={{ width: '15%' }}>Rate Info</th>
            <th className="number" style={{ width: '15%' }}>Cost/Piece (₹)</th>
            <th className="number" style={{ width: '15%' }}>Cost/Kg (₹)</th>
          </tr>
        </thead>
        <tbody>
          {(costing.process_costs || []).map((p, idx) => {
            const setupCost = getSetupCostForStep(p);
            const toolingCost = getToolingCostForStep(p);
            
            return (
              <React.Fragment key={p.process_name || `proc-${idx}`}>
                {/* Main Process Row */}
                <tr>
                  <td>
                    <strong>{idx + 1}. {p.process_name}</strong>
                    {p.machine_name && <div style={{ fontSize: '10px', color: '#64748b' }}>Machine: {p.machine_name}</div>}
                    {p.vendor_name && <div style={{ fontSize: '10px', color: '#c2410c' }}>Vendor: {p.vendor_name}</div>}
                  </td>
                  <td>
                    <span style={{ 
                      padding: '2px 6px', 
                      borderRadius: '4px', 
                      fontSize: '10px',
                      background: p.source === "outsourced" ? '#fed7aa' : '#dbeafe',
                      color: p.source === "outsourced" ? '#c2410c' : '#1e40af'
                    }}>
                      {p.source === "outsourced" ? "Outsourced" : "In-House"}
                    </span>
                  </td>
                  <td className="number" style={{ fontSize: '11px' }}>
                    {p.source === "outsourced" 
                      ? `${formatCurrency(p.vendor_rate)}/kg`
                      : `${p.output_per_hour || '-'} pcs/min @ ${formatCurrency(p.hourly_rate)}/hr`
                    }
                  </td>
                  <td className="number">{formatCurrency(p.cost_per_piece)}</td>
                  <td className="number">{getCostPerKg(p.cost_per_piece)}</td>
                </tr>
                
                {/* Setup Cost Sub-row (if linked to this step) */}
                {setupCost && (
                  <tr style={{ background: '#fef3c7' }}>
                    <td style={{ paddingLeft: '30px' }}>
                      <span style={{ color: '#92400e' }}>↳ Setup Cost</span>
                      <span style={{ fontSize: '10px', color: '#78350f', marginLeft: '8px' }}>
                        ({setupCost.setup_time_minutes || 0} min @ {formatCurrency(setupCost.hourly_rate)}/hr ÷ {costing.quantity?.toLocaleString()} pcs)
                      </span>
                    </td>
                    <td><span style={{ fontSize: '10px', color: '#92400e' }}>Setup</span></td>
                    <td className="number" style={{ fontSize: '10px' }}>-</td>
                    <td className="number" style={{ color: '#92400e' }}>{formatCurrency(setupCost.setup_cost_per_piece || setupCost.cost_per_piece)}</td>
                    <td className="number" style={{ color: '#92400e' }}>{getCostPerKg(setupCost.setup_cost_per_piece || setupCost.cost_per_piece)}</td>
                  </tr>
                )}
                
                {/* Tooling Cost Sub-row (if linked to this step) */}
                {toolingCost && (
                  <tr style={{ background: '#fce7f3' }}>
                    <td style={{ paddingLeft: '30px' }}>
                      <span style={{ color: '#9d174d' }}>↳ Tooling Cost</span>
                      <span style={{ fontSize: '10px', color: '#831843', marginLeft: '8px' }}>
                        ({formatCurrency(toolingCost.tool_cost)} ÷ {toolingCost.tool_life_pieces?.toLocaleString() || toolingCost.tool_life?.toLocaleString()} pcs)
                      </span>
                    </td>
                    <td><span style={{ fontSize: '10px', color: '#9d174d' }}>Tooling</span></td>
                    <td className="number" style={{ fontSize: '10px' }}>-</td>
                    <td className="number" style={{ color: '#9d174d' }}>{formatCurrency(toolingCost.cost_per_piece)}</td>
                    <td className="number" style={{ color: '#9d174d' }}>{getCostPerKg(toolingCost.cost_per_piece)}</td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
          
          {/* Unlinked Setup Costs (if any) */}
          {(costing.setup_costs || []).filter(sc => !isSetupCostLinked(sc)).map((sc, idx) => (
            <tr key={`setup-${idx}`} style={{ background: '#fef3c7' }}>
              <td>
                <span style={{ color: '#92400e' }}>Setup Cost {sc.process_name ? `(${sc.process_name})` : '(General)'}</span>
                <span style={{ fontSize: '10px', color: '#78350f', marginLeft: '8px' }}>
                  ({sc.setup_time_minutes || 0} min @ {formatCurrency(sc.hourly_rate)}/hr)
                </span>
              </td>
              <td><span style={{ fontSize: '10px', color: '#92400e' }}>Setup</span></td>
              <td className="number">-</td>
              <td className="number" style={{ color: '#92400e' }}>{formatCurrency(sc.setup_cost_per_piece || sc.cost_per_piece)}</td>
              <td className="number" style={{ color: '#92400e' }}>{getCostPerKg(sc.setup_cost_per_piece || sc.cost_per_piece)}</td>
            </tr>
          ))}
          
          {/* Unlinked Tooling Costs (if any) */}
          {(costing.tooling_costs || []).filter(tc => !isToolingCostLinked(tc)).map((tc, idx) => (
            <tr key={`tooling-${idx}`} style={{ background: '#fce7f3' }}>
              <td>
                <span style={{ color: '#9d174d' }}>Tooling Cost {tc.process_name ? `(${tc.process_name})` : '(General)'}</span>
                <span style={{ fontSize: '10px', color: '#831843', marginLeft: '8px' }}>
                  ({formatCurrency(tc.tool_cost)} ÷ {tc.tool_life_pieces?.toLocaleString() || tc.tool_life?.toLocaleString()} pcs)
                </span>
              </td>
              <td><span style={{ fontSize: '10px', color: '#9d174d' }}>Tooling</span></td>
              <td className="number">-</td>
              <td className="number" style={{ color: '#9d174d' }}>{formatCurrency(tc.cost_per_piece)}</td>
              <td className="number" style={{ color: '#9d174d' }}>{getCostPerKg(tc.cost_per_piece)}</td>
            </tr>
          ))}
          
          {/* Total Process Cost Row */}
          <tr style={{ background: "#e0f2fe" }}>
            <td colSpan="3"><strong>Total Process Cost</strong></td>
            <td className="number"><strong>{formatCurrency(totalProcessCost)}</strong></td>
            <td className="number"><strong>{getCostPerKg(totalProcessCost)}</strong></td>
          </tr>
        </tbody>
      </table>

      {/* Overhead & Other Costs */}
      <h3 style={{ fontSize: "14px", fontWeight: "bold", marginBottom: "12px", marginTop: "20px", color: "#1e40af", borderBottom: "2px solid #1e40af", paddingBottom: "6px" }}>
        3. OVERHEAD & OTHER COSTS
      </h3>
      <table className="items-table">
        <thead>
          <tr>
            <th>Cost Component</th>
            <th className="number">%</th>
            <th className="number">Cost/Piece (₹)</th>
            <th className="number">Cost/Kg (₹)</th>
          </tr>
        </thead>
        <tbody>
          <tr><td>Packaging</td><td className="number">-</td><td className="number">{formatCurrency(packagingCostPerPiece)}</td><td className="number">{getCostPerKg(packagingCostPerPiece)}</td></tr>
          <tr><td>Logistics</td><td className="number">-</td><td className="number">{formatCurrency(logisticsCostPerPiece)}</td><td className="number">{getCostPerKg(logisticsCostPerPiece)}</td></tr>
          <tr><td>Rejection (IRR + CRR)</td><td className="number">{(rejection.irr_percentage || 0) + (rejection.crr_percentage || 0)}%</td><td className="number">{formatCurrency(rejectionCostPerPiece)}</td><td className="number">{getCostPerKg(rejectionCostPerPiece)}</td></tr>
          <tr><td>Quality / Inspection</td><td className="number">{quality.inspection_testing_percentage || 0}%</td><td className="number">{formatCurrency(qualityCostPerPiece)}</td><td className="number">{getCostPerKg(qualityCostPerPiece)}</td></tr>
          <tr><td>Overhead</td><td className="number">{overhead.overhead_percentage || 0}%</td><td className="number">{formatCurrency(overheadCostPerPiece)}</td><td className="number">{getCostPerKg(overheadCostPerPiece)}</td></tr>
          <tr><td>Finance & Risk</td><td className="number">{(financeRisk.finance_cost_percentage || 0) + (financeRisk.risk_cost_percentage || 0)}%</td><td className="number">{formatCurrency(financeRiskCostPerPiece)}</td><td className="number">{getCostPerKg(financeRiskCostPerPiece)}</td></tr>
        </tbody>
      </table>

      {/* Final Summary */}
      <h3 style={{ fontSize: "14px", fontWeight: "bold", marginBottom: "12px", marginTop: "20px", color: "#059669", borderBottom: "2px solid #059669", paddingBottom: "6px" }}>
        4. FINAL SUMMARY
      </h3>
      <table className="items-table">
        <thead>
          <tr>
            <th>Cost Category</th>
            <th className="number">Cost/Piece (₹)</th>
            <th className="number">Cost/Kg (₹)</th>
          </tr>
        </thead>
        <tbody>
          <tr><td>Raw Material Cost</td><td className="number">{formatCurrency(rmCostPerPiece)}</td><td className="number">{getCostPerKg(rmCostPerPiece)}</td></tr>
          <tr><td>Process Cost (incl. Setup & Tooling)</td><td className="number">{formatCurrency(totalProcessCost)}</td><td className="number">{getCostPerKg(totalProcessCost)}</td></tr>
          <tr><td>Packaging & Logistics</td><td className="number">{formatCurrency(packagingCostPerPiece + logisticsCostPerPiece)}</td><td className="number">{getCostPerKg(packagingCostPerPiece + logisticsCostPerPiece)}</td></tr>
          <tr><td>Quality & Rejection</td><td className="number">{formatCurrency(qualityCostPerPiece + rejectionCostPerPiece)}</td><td className="number">{getCostPerKg(qualityCostPerPiece + rejectionCostPerPiece)}</td></tr>
          <tr><td>Overhead</td><td className="number">{formatCurrency(overheadCostPerPiece)}</td><td className="number">{getCostPerKg(overheadCostPerPiece)}</td></tr>
          <tr><td>Finance & Risk</td><td className="number">{formatCurrency(financeRiskCostPerPiece)}</td><td className="number">{getCostPerKg(financeRiskCostPerPiece)}</td></tr>
          <tr style={{ background: "#dcfce7", fontWeight: "bold" }}>
            <td>TOTAL COST</td>
            <td className="number">{formatCurrency(costing.total_cost_per_piece)}</td>
            <td className="number">{formatCurrency(costing.total_cost_per_kg)}</td>
          </tr>
          <tr><td>Profit Margin ({costing.margin_percentage || 0}%)</td><td className="number">{formatCurrency((costing.selling_price_per_piece || 0) - (costing.total_cost_per_piece || 0))}</td><td className="number">{getCostPerKg((costing.selling_price_per_piece || 0) - (costing.total_cost_per_piece || 0))}</td></tr>
          <tr style={{ background: "#dbeafe", fontWeight: "bold", fontSize: "14px" }}>
            <td style={{ color: "#1e40af" }}>SELLING PRICE</td>
            <td className="number" style={{ color: "#1e40af" }}>{formatCurrency(costing.selling_price_per_piece)}</td>
            <td className="number" style={{ color: "#1e40af" }}>{formatCurrency(costing.selling_price_per_kg)}</td>
          </tr>
        </tbody>
      </table>

      {/* Price per 100 and 1000 */}
      <div style={{ display: "flex", gap: "20px", marginTop: "20px" }}>
        <div className="info-box" style={{ flex: 1, textAlign: "center" }}>
          <h4>Cost per 1000 Pieces</h4>
          <p className="value" style={{ fontSize: "20px", color: "#059669", fontWeight: "bold" }} data-testid="cost-per-1000-pcs">
            {formatCurrency((costing.total_cost_per_piece || 0) * 1000)}
          </p>
        </div>
        <div className="info-box" style={{ flex: 1, textAlign: "center" }}>
          <h4>Selling Price per 1000 Pieces</h4>
          <p className="value" style={{ fontSize: "20px", color: "#1e40af", fontWeight: "bold" }} data-testid="sell-per-1000-pcs">
            {formatCurrency((costing.selling_price_per_piece || 0) * 1000)}
          </p>
        </div>
      </div>
      <div style={{ display: "flex", gap: "20px", marginTop: "10px" }}>
        <div className="info-box" style={{ flex: 1, textAlign: "center" }}>
          <h4>Cost per 100 Pieces</h4>
          <p className="value" style={{ fontSize: "16px" }}>{formatCurrency((costing.total_cost_per_piece || 0) * 100)}</p>
        </div>
        <div className="info-box" style={{ flex: 1, textAlign: "center" }}>
          <h4>Selling Price per 100 Pieces</h4>
          <p className="value" style={{ fontSize: "16px" }}>{formatCurrency((costing.selling_price_per_piece || 0) * 100)}</p>
        </div>
      </div>

      {/* Footer */}
      <div className="footer">
        <p>This is a computer-generated cost estimate. Prices are subject to change based on material costs and order quantity.</p>
        <p>Generated on {new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
      </div>
    </div>
  );
}


// Sales Order / Order Acceptance Print View

